import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getTodasAreas, logoutUsuario, fetchHorasArea } from '../../actions/index.js';
import NavBarHome from '../NavBarHome/NavBarHome.jsx';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner.jsx';
import Select from 'react-select';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LabelList
} from 'recharts';
import './ReporteArea.css';

// Paleta de colores para los gráficos (verde corporativo + variantes)
const CHART_COLORS = ['#28C76F', '#3FE07F', '#1E9E75', '#6CECA0', '#8A988F'];

// react-select con la paleta moderna oscura + verde
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#0A100D',
    borderColor: state.isFocused ? 'rgba(40,199,111,0.6)' : 'rgba(255,255,255,0.10)',
    borderWidth: '0.5px',
    borderRadius: '10px',
    color: '#E8EFEA',
    minHeight: '42px',
    height: '42px',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(40,199,111,0.20)' : 'none',
    '&:hover': { borderColor: 'rgba(40,199,111,0.45)' }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#0C120F',
    border: '0.5px solid rgba(40,199,111,0.25)',
    borderRadius: '11px',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.6)',
    zIndex: 9999,
  }),
  menuList: (provided) => ({ ...provided, padding: '6px', maxHeight: '230px' }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'rgba(40,199,111,0.16)' : state.isFocused ? 'rgba(40,199,111,0.12)' : 'transparent',
    color: state.isSelected || state.isFocused ? '#EAF6EF' : '#CBD6CF',
    borderRadius: '8px',
    padding: '9px 11px',
    cursor: 'pointer',
  }),
  singleValue: (provided) => ({ ...provided, color: '#E8EFEA', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  placeholder: (provided) => ({ ...provided, color: '#9FB0A6', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  input: (provided) => ({ ...provided, color: '#E8EFEA', fontSize: 13 }),
  valueContainer: (provided) => ({ ...provided, backgroundColor: 'transparent', flexWrap: 'nowrap', height: '42px', padding: '0 10px' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (provided, state) => ({ ...provided, color: state.isFocused ? '#3FE07F' : '#5C6862', '&:hover': { color: '#3FE07F' } }),
  clearIndicator: (provided) => ({ ...provided, color: '#9FB0A6', '&:hover': { color: '#E86D6D' } }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
};

const tooltipStyle = {
  background: '#0C120F',
  border: '0.5px solid rgba(40,199,111,0.3)',
  borderRadius: 10,
  color: '#E8EFEA',
  fontSize: 13,
};

const ReporteArea = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  
  // Selectors de Redux
  const user = useSelector(state => state.usuario);
  const areas = useSelector(state => state.Repoteareas);
  const horasReporte = useSelector(state => state.horasReporte);
  
  // Estados para filtros
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);
  const [areaSeleccionada, setAreaSeleccionada] = useState(null);
  
  // Estados para datos y UI
  const [horasConsultadas, setHorasConsultadas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mostrarTablas, setMostrarTablas] = useState(false);

  // useEffect para cargar datos iniciales
  useEffect(() => {
    console.log('📍 Cargando áreas...');
    dispatch(getTodasAreas());
  }, [dispatch]);

  // useEffect para actualizar horasConsultadas cuando cambien horasReporte
  useEffect(() => {
    if (horasReporte && Array.isArray(horasReporte)) {
      console.log('📊 Actualizando horas consultadas:', horasReporte);
      setHorasConsultadas(horasReporte);
      setMostrarTablas(true);
      setIsLoading(false);
    }
  }, [horasReporte]);

  // useEffect para debug áreas
  useEffect(() => {
    console.log('📍 Debug ReporteArea - Áreas disponibles:', areas);
  }, [areas]);

  // Función para manejar el cierre de sesión
  const handleLogout = () => {
    dispatch(logoutUsuario());
    history.push('/login');
  };

  // Función para formatear fecha para mostrar
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const [dia, mes, año] = fecha.split('/');
    return `${dia}/${mes}/${año}`;
  };

  // Función para formatear horas
  const formatearHoras = (horas) => {
    return parseFloat(horas).toFixed(1);
  };

  // Preparar opciones para el select de áreas
  const opcionesAreas = areas?.areas?.map(area => ({
    value: area.id,
    label: area.nombre
  })) || [];

  // Función para buscar horas por área
  const buscarHorasPorArea = async () => {
    if (!areaSeleccionada || !fechaInicio || !fechaFin) {
      alert('Por favor selecciona un área y ambas fechas');
      return;
    }

    setIsLoading(true);
    setMostrarTablas(false);

    try {
      // Formatear fechas para el backend (YYYY-MM-DD)
      const fechaInicioFormatted = fechaInicio.toISOString().split('T')[0];
      const fechaFinFormatted = fechaFin.toISOString().split('T')[0];

      console.log('🔍 Buscando horas:', {
        areaId: areaSeleccionada.value,
        fechaInicio: fechaInicioFormatted,
        fechaFin: fechaFinFormatted
      });

      await dispatch(fetchHorasArea(areaSeleccionada.value, fechaInicioFormatted, fechaFinFormatted));
      
    } catch (error) {
      console.error('❌ Error al buscar horas por área:', error);
      alert('Error al obtener las horas del área');
      setIsLoading(false);
    }
  };

  // Función para limpiar filtros
  const limpiarFiltros = () => {
    setAreaSeleccionada(null);
    setFechaInicio(null);
    setFechaFin(null);
    setHorasConsultadas([]);
    setMostrarTablas(false);
    setIsLoading(false);
  };

  // Función para exportar datos detallados a Excel
  const exportarDetalleAExcel = () => {
    if (horasConsultadas.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Preparar datos para Excel
    const datosExcel = horasConsultadas.map(hora => ({
      Fecha: formatearFecha(hora.fechaCarga),
      Área: areaSeleccionada?.label || 'Área seleccionada',
      Cliente: hora.cliente ? hora.cliente.nombre : 'Sin cliente',
      Usuario: hora.usuario ? `${hora.usuario.nombre} ${hora.usuario.apellidos}` : 'Usuario desconocido',
      Seniority: hora.senorityhora || 'N/A',
      Servicio: hora.servicio ? hora.servicio.nombre : 'Sin servicio',
      Tarea: hora.tarea ? hora.tarea.nombre : 'Sin tarea',
      Descripción: hora.descripcion || 'Sin descripción',
      Horas: parseFloat(hora.horas)
    }));

    // Crear libro de Excel
    const ws = utils.json_to_sheet(datosExcel);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Detalle Horas por Área');

    // Generar y descargar archivo
    const fechaActual = new Date().toLocaleDateString('es-ES').replace(/\//g, '-');
    const nombreArchivo = `Detalle_Horas_Area_${areaSeleccionada?.label || 'Area'}_${fechaActual}.xlsx`;
    
    const wbout = write(wb, { bookType: 'xlsx', type: 'binary' });
    
    function s2ab(s) {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
      return buf;
    }
    
    saveAs(new Blob([s2ab(wbout)], { type: 'application/octet-stream' }), nombreArchivo);
  };

  // Función para exportar resumen a Excel
  const exportarResumenAExcel = () => {
    if (horasConsultadas.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Usar la misma función calcularResumen para consistencia
    const resumenDatos = calcularResumen();
    
    // Convertir a array para Excel
    const datosResumen = resumenDatos.map(resumen => ({
      Usuario: resumen.usuario,
      Seniority: resumen.seniority,
      'Total Horas': parseFloat(resumen.totalHoras).toFixed(1),
      'Cantidad Registros': resumen.registros,
      '% del Total': resumen.porcentaje
    }));

    // Agregar totales
    const totalHoras = horasConsultadas.reduce((sum, hora) => sum + parseFloat(hora.horas), 0);
    datosResumen.push({
      Usuario: 'TOTAL GENERAL',
      Seniority: '',
      'Total Horas': totalHoras.toFixed(1),
      'Cantidad Registros': horasConsultadas.length,
      '% del Total': '100%'
    });

    // Crear libro de Excel
    const ws = utils.json_to_sheet(datosResumen);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Resumen Horas por Área');

    // Generar y descargar archivo
    const fechaActual = new Date().toLocaleDateString('es-ES').replace(/\//g, '-');
    const nombreArchivo = `Resumen_Horas_Area_${areaSeleccionada?.label || 'Area'}_${fechaActual}.xlsx`;
    
    const wbout = write(wb, { bookType: 'xlsx', type: 'binary' });
    
    function s2ab(s) {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
      return buf;
    }
    
    saveAs(new Blob([s2ab(wbout)], { type: 'application/octet-stream' }), nombreArchivo);
  };

  // Calcular resumen para mostrar en la tabla
  const calcularResumen = () => {
    const resumenPorUsuario = {};
    const totalGeneralHoras = horasConsultadas.reduce((sum, hora) => sum + parseFloat(hora.horas), 0);
    
    horasConsultadas.forEach(hora => {
      const nombreUsuario = hora.usuario ? `${hora.usuario.nombre} ${hora.usuario.apellidos}` : 'Usuario desconocido';
      const seniority = hora.senorityhora || 'N/A';
      
      if (!resumenPorUsuario[nombreUsuario]) {
        resumenPorUsuario[nombreUsuario] = {
          usuario: nombreUsuario,
          seniority: seniority,
          totalHoras: 0,
          registros: 0
        };
      }
      
      resumenPorUsuario[nombreUsuario].totalHoras += parseFloat(hora.horas);
      resumenPorUsuario[nombreUsuario].registros += 1;
    });

    // Agregar porcentaje a cada usuario
    return Object.values(resumenPorUsuario).map(resumen => ({
      ...resumen,
      porcentaje: totalGeneralHoras > 0 
        ? ((resumen.totalHoras / totalGeneralHoras) * 100).toFixed(1) + '%'
        : '0.0%'
    }));
  };

  // ===== Datos derivados para métricas y gráficos =====
  const totalHoras = useMemo(() => horasConsultadas.reduce((t, h) => t + parseFloat(h.horas || 0), 0), [horasConsultadas]);
  const registros = horasConsultadas.length;
  const usuariosUnicos = useMemo(() => new Set(horasConsultadas.map(h => (h.usuario ? `${h.usuario.nombre} ${h.usuario.apellidos}` : 'N/A'))).size, [horasConsultadas]);
  const clientesUnicos = useMemo(() => new Set(horasConsultadas.map(h => (h.cliente ? h.cliente.nombre : 'Sin cliente'))).size, [horasConsultadas]);

  const horasPorUsuario = useMemo(() => {
    const m = {};
    horasConsultadas.forEach(h => {
      const k = h.usuario ? `${h.usuario.nombre} ${h.usuario.apellidos}` : 'N/A';
      m[k] = (m[k] || 0) + parseFloat(h.horas || 0);
    });
    return Object.entries(m).map(([nombre, horas]) => ({ nombre, horas: Math.round(horas * 100) / 100 })).sort((a, b) => b.horas - a.horas).slice(0, 8);
  }, [horasConsultadas]);

  const horasPorCliente = useMemo(() => {
    const m = {};
    horasConsultadas.forEach(h => {
      const k = h.cliente ? h.cliente.nombre : 'Sin cliente';
      m[k] = (m[k] || 0) + parseFloat(h.horas || 0);
    });
    const arr = Object.entries(m).map(([nombre, horas]) => ({ nombre, horas: Math.round(horas * 100) / 100 })).sort((a, b) => b.horas - a.horas);
    if (arr.length <= 5) return arr;
    const top = arr.slice(0, 4);
    const otros = arr.slice(4).reduce((t, x) => t + x.horas, 0);
    return [...top, { nombre: 'Otros', horas: Math.round(otros * 100) / 100 }];
  }, [horasConsultadas]);

  const abreviar = (v) => { const s = String(v); return s.length > 12 ? s.slice(0, 11) + '…' : s; };

  return (
    <div className="reporte-area-container">
      <NavBarHome user={user} handleLogout={handleLogout} />
      
      {isLoading && <LoadingSpinner />}
      
      <div className="reporte-area-content">
        <div className="reporte-area-header">
          <h1>Reporte por área</h1>
          <p>Consultá y analizá las horas trabajadas por área en un período.</p>
        </div>

        <div className="reporte-area-filtros-container">
          <div className="reporte-area-filtros-layout">
            <div className="reporte-area-form-group">
              <label>Área</label>
              <Select
                styles={customSelectStyles}
                value={areaSeleccionada}
                onChange={setAreaSeleccionada}
                options={opcionesAreas}
                placeholder="Seleccioná un área"
                isClearable
                className="reporte-area-select-area"
                classNamePrefix="rep-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isSearchable
              />
            </div>

            <div className="reporte-area-form-group">
              <label>Fecha inicio</label>
              <div className="reporte-area-datepicker-wrapper">
                <DatePicker
                  selected={fechaInicio}
                  onChange={(date) => setFechaInicio(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Selecciona fecha inicio"
                  showPopperArrow={false}
                  autoComplete="off"
                  maxDate={new Date()}
                  popperPlacement="bottom-start"
                  popperModifiers={{
                    preventOverflow: {
                      enabled: true,
                      escapeWithReference: false,
                      boundariesElement: 'viewport'
                    }
                  }}
                  fixedHeight
                />
              </div>
            </div>

            <div className="reporte-area-form-group">
              <label>Fecha fin</label>
              <div className="reporte-area-datepicker-wrapper">
                <DatePicker
                  selected={fechaFin}
                  onChange={(date) => setFechaFin(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Selecciona fecha fin"
                  showPopperArrow={false}
                  autoComplete="off"
                  maxDate={new Date()}
                  minDate={fechaInicio}
                />
              </div>
            </div>

            <div className="reporte-area-botones-accion">
              <button
                className="reporte-area-btn-buscar"
                onClick={buscarHorasPorArea}
                disabled={!areaSeleccionada || !fechaInicio || !fechaFin || isLoading}
              >
                {isLoading ? 'Buscando...' : 'Buscar'}
              </button>

              <button
                className="reporte-area-btn-limpiar"
                onClick={limpiarFiltros}
                disabled={isLoading}
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>

        {mostrarTablas && horasConsultadas.length > 0 && (
          <div className="resultados-container">
            {/* Métricas */}
            <div className="rep-metrics">
              <div className="rep-metric"><span className="rep-metric-lbl">Total de horas</span><span className="rep-metric-val green">{Math.round(totalHoras * 100) / 100} h</span><span className="rep-metric-sub">en el período</span></div>
              <div className="rep-metric"><span className="rep-metric-lbl">Registros</span><span className="rep-metric-val">{registros}</span><span className="rep-metric-sub">cargas de horas</span></div>
              <div className="rep-metric"><span className="rep-metric-lbl">Usuarios</span><span className="rep-metric-val">{usuariosUnicos}</span><span className="rep-metric-sub">con actividad</span></div>
              <div className="rep-metric"><span className="rep-metric-lbl">Clientes</span><span className="rep-metric-val">{clientesUnicos}</span><span className="rep-metric-sub">involucrados</span></div>
            </div>

            {/* Gráficos */}
            <div className="rep-charts">
              <div className="rep-card">
                <p className="rep-chart-title">Horas por usuario</p>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={horasPorUsuario} margin={{ top: 24, right: 8, left: -8, bottom: 4 }} barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="nombre" interval={0} tickFormatter={abreviar} tick={{ fill: '#8A988F', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} />
                    <YAxis tick={{ fill: '#8A988F', fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip cursor={{ fill: 'rgba(40,199,111,0.08)' }} contentStyle={tooltipStyle} itemStyle={{ color: '#E8EFEA' }} labelStyle={{ color: '#9fb0a6' }} formatter={(v) => [`${v} h`, 'Horas']} />
                    <Bar dataKey="horas" fill="#28C76F" radius={[6, 6, 0, 0]} maxBarSize={56}>
                      <LabelList dataKey="horas" position="top" fill="#3FE07F" fontSize={12} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="rep-card">
                <p className="rep-chart-title">Distribución por cliente</p>
                <div className="rep-donut-row">
                  <div className="rep-donut-chart">
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={horasPorCliente} dataKey="horas" nameKey="nombre" cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={2} stroke="none">
                          {horasPorCliente.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: '#E8EFEA' }} labelStyle={{ color: '#9fb0a6' }} formatter={(v, n) => [`${v} h`, n]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="rep-legend">
                    {horasPorCliente.map((e, i) => (
                      <div className="rep-legend-li" key={i}>
                        <span className="rep-legend-dot" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}></span>
                        <span className="rep-legend-name">{e.nombre}</span>
                        <span className="rep-legend-pct">{totalHoras ? Math.round((e.horas / totalHoras) * 100) : 0}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Tabla Resumen por Usuario */}
            <div className="tabla-container">
              <div className="tabla-header">
                <h3>Resumen por Usuario</h3>
                <button 
                  className="reporte-area-btn-exportar"
                  onClick={exportarResumenAExcel}
                >
                  Exportar resumen
                </button>
              </div>
              
              <div className="tabla-wrapper">
                <table className="tabla-resumen">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Seniority</th>
                      <th>Total Horas</th>
                      <th>Registros</th>
                      <th>% del Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calcularResumen().map((resumen, index) => (
                      <tr key={index}>
                        <td>{resumen.usuario}</td>
                        <td>{resumen.seniority}</td>
                        <td className="reporte-area-horas">{formatearHoras(resumen.totalHoras)}</td>
                        <td>{resumen.registros}</td>
                        <td className={resumen.porcentaje === '100.0%' ? 'porcentaje-completo' : 'reporte-area-porcentaje-badge'}>
                          {resumen.porcentaje}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td>TOTAL</td>
                      <td></td>
                      <td className="reporte-area-horas-cell">{formatearHoras(horasConsultadas.reduce((sum, hora) => sum + parseFloat(hora.horas), 0))}</td>
                      <td>{horasConsultadas.length}</td>
                      <td className="porcentaje-completo">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Tabla Detalle */}
            <div className="tabla-container">
              <div className="tabla-header">
                <h3>Detalle de registros</h3>
                <button
                  className="reporte-area-btn-exportar"
                  onClick={exportarDetalleAExcel}
                >
                  Exportar detalle
                </button>
              </div>
              
              <div className="tabla-wrapper">
                <table className="tabla-detalle">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Usuario</th>
                      <th>Cliente</th>
                      <th>Servicio</th>
                      <th>Tarea</th>
                      <th>Descripción</th>
                      <th>Horas</th>
                      <th>Seniority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {horasConsultadas.map((hora, index) => (
                      <tr key={hora.id || index}>
                        <td>{formatearFecha(hora.fechaCarga)}</td>
                        <td>{hora.usuario ? `${hora.usuario.nombre} ${hora.usuario.apellidos}` : 'N/A'}</td>
                        <td>{hora.cliente ? hora.cliente.nombre : 'Sin cliente'}</td>
                        <td>{hora.servicio ? hora.servicio.nombre : 'Sin servicio'}</td>
                        <td>{hora.tarea ? hora.tarea.nombre : 'Sin tarea'}</td>
                        <td className="descripcion-cell" title={hora.descripcion}>
                          {hora.descripcion || 'Sin descripción'}
                        </td>
                        <td className="reporte-area-horas">{formatearHoras(hora.horas)}</td>
                        <td>{hora.senorityhora || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td colSpan="6">TOTAL HORAS</td>
                      <td className="reporte-area-horas-cell">{formatearHoras(horasConsultadas.reduce((sum, hora) => sum + parseFloat(hora.horas), 0))}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

        {mostrarTablas && horasConsultadas.length === 0 && (
          <div className="sin-resultados">
            <h3>Sin resultados</h3>
            <p>No se encontraron registros de horas para el área y período seleccionados.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReporteArea;